import React, { useEffect, useRef, useState } from 'react';
import wireWaDialog from '../wireWaDialog';
import './QuickAddTaskDialog.css';

/**
 * Quick-add task — a wa-dialog that never leaves the lesson (Anthony's decision,
 * design handoff Ph 5). A teacher who thinks of something mid-lesson ("print the
 * duet parts", "email the parent about recital shoes") captures it in two taps
 * without navigating away. Minimal by design: a title and optional notes; the
 * student is auto-attached as context and the creating teacher as provenance.
 * Area/priority are left unset so it lands in the triage queue like any capture.
 *
 * wa-dialog is controlled the house way (see DeliverDialog): a ref, `.open` as a
 * property, and a wa-after-hide listener so ESC/backdrop closes reset the form.
 */
const QuickAddTaskDialog = ({ open, studentName = '', onCreate, onClose }) => {
	const dialogRef = useRef(null);
	const [title, setTitle] = useState('');
	const [notes, setNotes] = useState('');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	// Drive open/close as a property, not an attribute (WA + React 18).
	useEffect(() => {
		if (dialogRef.current) dialogRef.current.open = open;
	}, [open]);

	// Reset the form whenever the dialog opens fresh.
	useEffect(() => {
		if (open) {
			setTitle('');
			setNotes('');
			setError('');
			setSaving(false);
		}
	}, [open]);

	const submit = async () => {
		const trimmed = title.trim();
		if (!trimmed) {
			setError('Give the task a title.');
			return;
		}
		setSaving(true);
		setError('');
		try {
			await onCreate({ title: trimmed, notes: notes.trim() });
			onClose();
		} catch (err) {
			console.error('Error creating lesson task:', err);
			setError('Could not save the task. Try again.');
			setSaving(false);
		}
	};

	return (
		<wa-dialog ref={wireWaDialog(dialogRef, onClose)} label="Add a task" class="ld-qtask">
			<div className="ld-qtask__body">
				{studentName && (
					<p className="ld-qtask__context">
						<i className="fa-sharp fa-solid fa-user" aria-hidden="true" /> For {studentName}
					</p>
				)}
				<label className="ld-qtask__label" htmlFor="ld-qtask-title">
					Task
				</label>
				<input
					id="ld-qtask-title"
					className="ld-qtask__input"
					type="text"
					value={title}
					placeholder="e.g. Print the duet parts"
					onChange={(e) => setTitle(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') submit();
					}}
				/>

				<label className="ld-qtask__label" htmlFor="ld-qtask-notes">
					Notes <span className="ld-qtask__optional">optional</span>
				</label>
				<textarea
					id="ld-qtask-notes"
					className="ld-qtask__notes"
					rows={2}
					value={notes}
					placeholder="Anything that helps future-you."
					onChange={(e) => setNotes(e.target.value)}
				/>

				{error && (
					<p className="ld-qtask__error" role="alert">
						{error}
					</p>
				)}
			</div>

			<button
				type="button"
				slot="footer"
				className="ld-qtask__btn ld-qtask__btn--ghost"
				onClick={onClose}>
				Cancel
			</button>
			<button
				type="button"
				slot="footer"
				className="ld-qtask__btn ld-qtask__btn--save"
				disabled={saving}
				onClick={submit}>
				<i className="fa-sharp fa-solid fa-plus" aria-hidden="true" />
				{saving ? 'Adding…' : 'Add task'}
			</button>
		</wa-dialog>
	);
};

export default QuickAddTaskDialog;
